import React, { useState } from 'react';
import { schedulerApi } from '../api';
import { Alert } from '../components/ui';

export default function DevTools() {
  const [results, setResults] = useState({});

  const trigger = async (key, fn, label) => {
    setResults(r => ({ ...r, [key]: { status: 'running', message: 'Running...' } }));
    try {
      await fn();
      setResults(r => ({ ...r, [key]: { status: 'success', message: label + ' completed successfully' } }));
    } catch (e) {
      setResults(r => ({ ...r, [key]: { status: 'error', message: e.response?.data?.message || e.message } }));
    }
  };

  const jobs = [
    {
      key:   'overdue',
      label: 'Overdue Detection',
      desc:  'Finds all tasks past their deadline with status not DONE, marks them OVERDUE, and fires DEADLINE_MISSED notifications to the assignee, dept Admin, and Super Admin.',
      fn:    schedulerApi.runOverdue,
    },
    {
      key:   'digest',
      label: 'Daily Digest',
      desc:  'Sends the morning digest notification to every active user. Super Admin sees company-wide stats, Admin sees department stats, Employee sees their own task summary.',
      fn:    schedulerApi.runDigest,
    },
    {
      key:   'onhold',
      label: 'On-Hold Escalation',
      desc:  'Checks all ON_HOLD tasks. If any have been on hold longer than the configured threshold, fires escalation alerts to the employee, their dept Admin, and Super Admin.',
      fn:    schedulerApi.runOnHold,
    },
    {
      key:   'reminders',
      label: 'Reminder Alerts',
      desc:  'Fires all due personal reminders and creates REMINDER notifications (same as the every-minute scheduler). Use after setting a reminder a minute or two ahead to test pop-up alerts.',
      fn:    schedulerApi.runReminders,
    },
    {
      key:   'company-status',
      label: 'Company Subscription Check',
      desc:  'Finds ACTIVE and TRIAL companies whose subscription end date has passed and auto-suspends them. Logs each suspension in the God Audit Log.',
      fn:    schedulerApi.runCompanyStatus,
    },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '680px' }}>
      <h1 style={{ fontSize: '22px', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '4px' }}>
        Developer Tools
      </h1>
      <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '24px', lineHeight: 1.5 }}>
        Manually trigger scheduled jobs. In production these run automatically on their cron schedule.
        Use this during development to test the notification system without waiting overnight.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {jobs.map(job => (
          <div key={job.key} style={{
            background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--color-border)', padding: '18px 20px',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 600, fontSize: '15px', marginBottom: '4px' }}>{job.label}</p>
                <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>{job.desc}</p>
              </div>
              <button
                onClick={() => trigger(job.key, job.fn, job.label)}
                disabled={results[job.key]?.status === 'running'}
                style={{
                  padding: '9px 16px', borderRadius: 'var(--radius-md)',
                  background: results[job.key]?.status === 'running' ? 'var(--color-border)' : 'var(--color-primary)',
                  color: '#fff', border: 'none', cursor: results[job.key]?.status === 'running' ? 'not-allowed' : 'pointer',
                  fontSize: '13px', fontWeight: 500, flexShrink: 0, minWidth: '80px',
                }}
              >
                {results[job.key]?.status === 'running' ? '...' : 'Run Now'}
              </button>
            </div>

            {results[job.key] && results[job.key].status !== 'running' && (
              <div style={{ marginTop: '10px' }}>
                <Alert
                  type={results[job.key].status === 'success' ? 'success' : 'error'}
                  message={results[job.key].message}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: '24px', padding: '16px', background: 'var(--color-bg)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
        <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', fontWeight: 600, marginBottom: '6px' }}>
          Production Cron Schedule
        </p>
        <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', lineHeight: 2, fontFamily: 'monospace' }}>
          <p>Overdue Detection  · 07:00 every day</p>
          <p>Daily Digest       · 09:00 Mon–Sat</p>
          <p>On-Hold Check      · Every hour 08:00–18:00 Mon–Sat</p>
        </div>
      </div>
    </div>
  );
}
